import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useB2C } from "@/context/B2CAuthContext";
import { labelFor, CATEGORIES } from "@/lib/categories";
import { COUNTRIES, countryFlag, countryName } from "@/lib/countries";
import { useLang } from "@/context/LanguageContext";
import { usePageTitle } from "@/hooks/usePageTitle";
import { CountUp } from "@/components/CountUp";
import { useTicker } from "@/lib/boost";
import { BoostedCarousel } from "@/components/BoostedCarousel";
import { BoostPromoBanner } from "@/components/BoostPromoBanner";
import { ReportAdButton } from "@/components/ReportAdButton";
import Pagination, { paginate } from "@/components/Pagination";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Search, PackageSearch, Sparkles, Gift, MapPin, Facebook, MessageCircle, Link2, ImageIcon, CheckCircle2, Clock, HandHeart, PackageCheck } from "lucide-react";
import { XIcon } from "@/components/XIcon";
import { FalseDeclarationWarning } from "@/components/FalseDeclarationWarning";
import { InstagramIcon, TikTokIcon } from "@/components/BrandIcons";
import { CategoryAvatar } from "@/components/CategoryAvatar";
import { AdCityFilter } from "@/components/AdCityFilter";
import FinderDeclarationDialog from "@/components/FinderDeclarationDialog";
import ImageUpload from "@/components/ImageUpload";
import { FindoSpinner } from "@/components/FindoSpinner";

const BANNER_FOUND = "https://static.prod-images.emergentagent.com/jobs/23603454-b3c7-441a-b8dd-7df2a2693c30/images/488eae03e905a1d31bee227880dc5a6c2400365c7ba4b1983aec929ca0308028.jpeg";
const BANNER_LOST = "https://static.prod-images.emergentagent.com/jobs/23603454-b3c7-441a-b8dd-7df2a2693c30/images/d592a72c0833fc3c3909df5cec7726abbd7dd230fd04abe4d2ba5d10e099c11c.jpeg";

export default function Annonces({ adType = "lost" }) {
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const { user } = useB2C();
  const isFound = adType === "found";
  usePageTitle(t(isFound ? "anf.title" : "an.title"));
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("all");
  const [city, setCity] = useState("");
  const [sort, setSort] = useState("recent");
  const [quick, setQuick] = useState("all"); // all | boosted | photo | resolved
  const [country, setCountry] = useState(null); // resolved default (account → IP → FR)
  const [contactFor, setContactFor] = useState(null);
  const [page, setPage] = useState(1);
  const countryTriggerRef = useRef(null);
  const openCountry = () => {
    countryTriggerRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    setTimeout(() => countryTriggerRef.current?.click(), 320);
  };
  useEffect(() => { setPage(1); }, [q, cat, city, adType, sort, country, quick]);
  useTicker();

  // Resolve the default country once: account preference → IP geolocation → FR.
  useEffect(() => {
    if (country !== null) return;
    let alive = true;
    (async () => {
      let c = user?.country;
      if (!c) {
        try { const r = await api("/geo/my-country", { auth: false }); c = r.country; } catch { /* ignore */ }
      }
      if (alive) setCountry(c || "FR");
    })();
    return () => { alive = false; };
  }, [user, country]);

  // Attribution obligatoire : la connexion est requise pour signaler un objet
  // (évite les annonces "orphelines" sans compte pour suivre la restitution).
  const [warningFor, setWarningFor] = useState(null);
  // « Attention » first, then login check, then the existing contact dialog.
  const openContact = (a) => {
    setWarningFor(a);
  };
  const onWarningContinue = () => {
    const a = warningFor;
    setWarningFor(null);
    if (!user) {
      toast.error(t("an.loginRequired"));
      navigate(`/mon-compte?next=${encodeURIComponent(`/annonces/${a.id}?action=contact`)}`);
      return;
    }
    setContactFor(a);
  };

  useEffect(() => {
    if (country === null) return;
    setItems(null);
    // A typed city searches every country; otherwise scope to the selected country.
    const cp = city.trim() ? `&city=${encodeURIComponent(city.trim())}`
      : country === "ALL" ? "" : `&country=${encodeURIComponent(country)}`;
    api(`/public-ads?ad_type=${adType}${cp}`, { auth: false }).then((r) => setItems(r.items || [])).catch(() => setItems([]));
  }, [adType, country, lang, city]);

  const boosted = useMemo(() => (items || []).filter((a) => a.boosted), [items]);
  const catCounts = useMemo(() => {
    const m = {};
    (items || []).forEach((a) => { if (a.category) m[a.category] = (m[a.category] || 0) + 1; });
    return m;
  }, [items]);
  const sortedCats = useMemo(() => [...CATEGORIES].sort((a, b) => a.label.localeCompare(b.label)), []);

  const filtered = useMemo(() => {
    if (!items) return [];
    const s = q.trim().toLowerCase();
    const c = city.trim().toLowerCase();
    const arr = items.filter((a) => {
      if (quick === "boosted") { if (!a.boosted) return false; }
      else if (a.boosted) return false;
      if (quick === "photo" && !a.image_base64) return false;
      if (quick === "resolved" && !a.match_resolved) return false;
      if (quick === "recent7") {
        const wk = Date.now() - 7 * 864e5;
        if (new Date(a.created_at || a.lost_date || a.date || 0).getTime() < wk) return false;
      }
      if (s && !`${a.description} ${a.lost_place} ${labelFor(a.category)}`.toLowerCase().includes(s)) return false;
      if (cat !== "all" && a.category !== cat) return false;
      if (c && !(a.lost_place || a.city || "").toLowerCase().includes(c)) return false;
      return true;
    });
    const dateOf = (a) => new Date(a.created_at || a.lost_date || a.date || 0).getTime();
    if (sort === "relevance" && s) {
      const score = (a) => {
        const i = `${a.description} ${a.lost_place} ${labelFor(a.category)}`.toLowerCase().indexOf(s);
        return i < 0 ? 1e9 : i;
      };
      arr.sort((a, b) => score(a) - score(b) || dateOf(b) - dateOf(a));
    } else {
      arr.sort((a, b) => dateOf(b) - dateOf(a));
    }
    return arr;
  }, [items, q, cat, city, sort, quick]);
  const pg = paginate(filtered, page);

  return (
    <div className="bg-background">
      <div className="relative h-72 sm:h-80 overflow-hidden photo-scrim">
        <img src={isFound ? BANNER_FOUND : BANNER_LOST} alt="" className={`w-full h-full object-cover animate-ken-burns ${isFound ? "object-[center_48%]" : "object-[center_84%]"}`} data-testid="ads-banner-image" />
        <div className="absolute inset-0 bg-[#191A54]/45" aria-hidden="true" />
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center text-white px-6">
          <span className={`inline-flex items-center gap-2 rounded-full px-3.5 py-1.5 text-xs font-bold uppercase tracking-wide ${isFound ? "bg-teal-600" : "badge-red"}`} data-testid="ads-badge">{isFound ? t("anf.badge") : t("an.badge")}</span>
          <h1 className="mt-4 font-display text-4xl md:text-5xl font-semibold">{isFound ? t("anf.title") : t("an.title")}</h1>
          <p className="mt-2 text-white/85 max-w-xl">{isFound ? t("anf.subtitle") : t("an.subtitle")}</p>
        </div>
      </div>

      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 py-10">
        {!isFound && (
        <div className="mb-8">
          <BoostPromoBanner
            testid="ads-boost-promo"
            title={t("pub.boostAdTitle")}
            subtitle={t("pub.boostCarouselSub")}
            ctaLabel={t("pub.boostAdCta")}
            to="/mon-compte"
          />
        </div>
        )}
        {boosted.length > 0 && (
          <div className="mb-10" data-testid="ads-boosted-top">
            <div className="flex items-center gap-2 mb-4">
              <span className="boost-shine inline-flex items-center gap-1.5 rounded-full bg-findo-red text-white px-3 py-1 text-xs font-bold uppercase tracking-wide" data-testid="boosted-section-badge">
                <Sparkles size={13} /> {t("an.boosted")}
              </span>
              <span className="text-sm text-slate-400">{t("an.badge")}</span>
            </div>
            <BoostedCarousel
              items={boosted}
              keyFor={(a) => a.id}
              itemClassName="w-80"
              speed={1.8}
              testid="ads-boosted-carousel"
              renderItem={(a) => <AdCard ad={a} t={t} isFound={isFound} onContact={() => openContact(a)} />}
            />
          </div>
        )}

        {country && (
          <div className="mb-4 inline-flex items-center gap-2 text-sm text-slate-600 bg-slate-50 border border-border rounded-full px-4 py-2" data-testid="ads-geo-banner">
            <MapPin size={14} className="text-findo-navy" />
            {country === "ALL" ? (
              <span data-testid="ads-geo-world">{t("an.geoBannerWorld")}</span>
            ) : (
              <span>{t("an.geoBanner")} <b className="text-findo-navy" data-testid="ads-geo-country">{countryFlag(country)} {countryName(country)}</b></span>
            )}
            <span className="text-slate-300">·</span>
            <button type="button" onClick={openCountry} className="font-semibold text-findo-navy hover:underline" data-testid="ads-geo-change">{t("an.geoChange")}</button>
          </div>
        )}

        <div className="flex flex-wrap items-center gap-3 mb-8">
          <div className="relative flex-1 min-w-[220px]">
            <Search size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("an.searchPh")} className="pl-10 h-12 rounded-full" data-testid="ads-search" />
          </div>
          <Select value={cat} onValueChange={setCat}>
            <SelectTrigger className="h-12 rounded-full w-48 bg-white" data-testid="ads-filter-category"><SelectValue placeholder={t("dec.category")} /></SelectTrigger>
            <SelectContent className="max-h-64">
              <SelectItem value="all">{t("dec.category")}</SelectItem>
              {sortedCats.map((c) => <SelectItem key={c.key} value={c.key}>{c.label}{catCounts[c.key] ? ` (${catCounts[c.key]})` : ""}</SelectItem>)}
            </SelectContent>
          </Select>
          <AdCityFilter value={city} onChange={setCity} adType={adType} placeholder={t("par.allCities")} />
          {city.trim() && (
            <Link to={`/carte?city=${encodeURIComponent(city.trim())}&type=${isFound ? "found" : "lost"}`} data-testid="ads-view-on-map"
              className="h-12 inline-flex items-center gap-1.5 rounded-full border border-border bg-white px-4 text-sm font-semibold text-findo-navy hover:bg-slate-50 transition-colors">
              <MapPin size={15} /> {t("an.viewOnMap")}
            </Link>
          )}
          <Select value={country || "FR"} onValueChange={setCountry}>
            <SelectTrigger ref={countryTriggerRef} className="h-12 rounded-full w-48 bg-white" data-testid="ads-filter-country"><SelectValue placeholder={t("an.country")} /></SelectTrigger>
            <SelectContent className="max-h-72 country-scroll">
              <SelectItem value="ALL" data-testid="ads-country-all">🌍 {t("an.allCountries")}</SelectItem>
              {COUNTRIES.map((c) => (
                <SelectItem key={c.code} value={c.code} data-testid={`ads-country-${c.code}`}>{c.flag} {c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger className="h-12 rounded-full w-44 bg-white" data-testid="ads-sort"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="recent" data-testid="ads-sort-recent">{t("an.sortRecent")}</SelectItem>
              <SelectItem value="relevance" data-testid="ads-sort-relevance">{t("an.sortRelevance")}</SelectItem>
            </SelectContent>
          </Select>
          <Link to={isFound ? "/annonces/trouvee" : "/declarer"}>
            <Button className={`h-12 rounded-full font-semibold ${isFound ? "bg-teal-600 hover:bg-teal-600/90" : "bg-findo-red hover:bg-findo-red/90"}`} data-testid="ads-post-btn">
              <Sparkles size={16} className="mr-1.5" /> {isFound ? t("anf.post") : t("an.post")}
            </Button>
          </Link>
        </div>

        {items !== null && (
          <div className="flex flex-wrap items-center gap-2 mb-6" data-testid="ads-quick-filters">
            {[
              { key: "all", label: t("an.qfAll") === "an.qfAll" ? "Toutes" : t("an.qfAll"), Icon: null },
              { key: "recent7", label: t("an.qfRecent") === "an.qfRecent" ? "Cette semaine" : t("an.qfRecent"), Icon: Clock },
              { key: "boosted", label: t("an.qfBoosted") === "an.qfBoosted" ? "Boostées" : t("an.qfBoosted"), Icon: Sparkles },
              { key: "photo", label: t("an.qfPhoto") === "an.qfPhoto" ? "Avec photo" : t("an.qfPhoto"), Icon: ImageIcon },
              ...(!isFound ? [{ key: "resolved", label: t("an.qfResolved") === "an.qfResolved" ? "Résolues" : t("an.qfResolved"), Icon: CheckCircle2 }] : []),
            ].map(({ key, label, Icon }) => (
              <button key={key} type="button" onClick={() => setQuick(key)} data-testid={`ads-quick-${key}`}
                className={`inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-sm font-semibold transition-colors border ${quick === key ? "bg-findo-navy text-white border-findo-navy" : "bg-white text-slate-600 border-border hover:border-findo-navy hover:text-findo-navy"}`}>
                {Icon && <Icon size={14} />} {label}
              </button>
            ))}
          </div>
        )}

        {items === null ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-4" data-testid="ads-skeleton">
            {Array.from({ length: 10 }).map((_, i) => (
              <div key={i} className="bg-white rounded-2xl overflow-hidden border border-border card-shadow">
                <div className="h-36 bg-slate-100 animate-pulse" />
                <div className="p-4 space-y-2.5">
                  <div className="h-2.5 w-1/3 rounded bg-slate-100 animate-pulse" />
                  <div className="h-3.5 w-4/5 rounded bg-slate-100 animate-pulse" />
                  <div className="h-2.5 w-1/2 rounded bg-slate-100 animate-pulse" />
                  <div className="h-9 w-full rounded-full bg-slate-100 animate-pulse mt-1" />
                </div>
              </div>
            ))}
          </div>
        ) : filtered.length === 0 ? (
          (boosted.length === 0 || quick !== "all") ? (
            <div className="text-center py-24 border-2 border-dashed border-border rounded-2xl">
              <div className="mx-auto h-16 w-16 grid place-items-center rounded-2xl bg-findo-navy/5 text-findo-navy/50"><PackageSearch size={32} /></div>
              <p className="mt-4 font-display font-bold text-lg text-slate-800">{t("an.none")}</p>
              <p className="mt-1 text-slate-500 text-sm max-w-sm mx-auto">{isFound ? t("anf.subtitle") : t("an.subtitle")}</p>
            </div>
          ) : null
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-4" data-testid="ads-grid">
            {pg.pageItems.map((a, i) => <AdCard key={a.id} ad={a} t={t} isFound={isFound} index={i} onContact={() => openContact(a)} />)}
          </div>
        )}
        <Pagination page={pg.page} pages={pg.pages} total={filtered.length} onPage={setPage} testid="ads-pagination" />
      </div>

      <FalseDeclarationWarning found={isFound} open={!!warningFor} onCancel={() => setWarningFor(null)} onContinue={onWarningContinue} />
      {contactFor?.flow_v2 ? (
        <FinderDeclarationDialog ad={contactFor} user={user} onClose={() => setContactFor(null)} />
      ) : (
        <ContactDialog ad={contactFor} isFound={isFound} onClose={() => setContactFor(null)} t={t} user={user} />
      )}
    </div>
  );
}

function ShareButtons({ ad, t }) {
  const url = `${process.env.REACT_APP_BACKEND_URL}/api/s/${ad.id}`;
  const text = `${t("an.badge")} — ${labelFor(ad.category)}: ${ad.description}`;
  const enc = encodeURIComponent;
  const links = {
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${enc(url)}`,
    twitter: `https://twitter.com/intent/tweet?url=${enc(url)}&text=${enc(text)}`,
    whatsapp: `https://wa.me/?text=${enc(text + " " + url)}`,
  };

  function open(href) {
    window.open(href, "_blank", "noopener,noreferrer,width=600,height=600");
  }

  async function copy() {
    try {
      await navigator.clipboard.writeText(url);
      toast.success(t("an.linkCopied"));
    } catch {
      toast.error(t("an.copyFailed"));
    }
  }
  async function shareSocial(label) {
    if (navigator.share) {
      try { await navigator.share({ title: "findloop", text, url }); return; }
      catch (e) { if (e && e.name === "AbortError") return; }
    }
    try { await navigator.clipboard.writeText(url); toast.success(t("hc.linkCopiedShareOn").replace("{label}", label)); }
    catch { toast.error(t("an.copyFailed")); }
  }

  const btn = "h-8 w-8 grid place-items-center rounded-full border border-border text-slate-500 hover:text-findo-navy hover:border-findo-navy transition-colors";

  return (
    <div className="mt-3 flex items-center gap-2 border-t border-border pt-3" data-testid="ad-share">
      <span className="text-xs text-slate-400 font-medium mr-1">{t("mc.share")}</span>
      <button type="button" onClick={() => open(links.facebook)} className={btn} style={{ color: "#1877F2" }} title="Facebook" data-testid="share-facebook"><Facebook size={15} /></button>
      <button type="button" onClick={() => open(links.twitter)} className={btn} style={{ color: "#000000" }} title="X / Twitter" data-testid="share-twitter"><XIcon size={15} /></button>
      <button type="button" onClick={() => open(links.whatsapp)} className={btn} style={{ color: "#25D366" }} title="WhatsApp" data-testid="share-whatsapp"><MessageCircle size={15} /></button>
      <button type="button" onClick={() => shareSocial("Instagram")} className={btn} title="Instagram" data-testid="share-instagram"><InstagramIcon size={15} /></button>
      <button type="button" onClick={() => shareSocial("TikTok")} className={btn} title="TikTok" data-testid="share-tiktok"><TikTokIcon size={15} /></button>
      <button type="button" onClick={copy} className={btn} title={t("an.copyLink")} data-testid="share-copy"><Link2 size={15} /></button>
    </div>
  );
}

function AdCard({ ad, t, isFound = false, index = 0, onContact }) {
  useEffect(() => {
    const key = `findo_ad_view_${ad.id}`;
    if (!sessionStorage.getItem(key)) {
      sessionStorage.setItem(key, "1");
      api(`/public-ads/${ad.id}/view`, { method: "POST", auth: false }).catch(() => {});
    }
  }, [ad.id]);
  return (
    <div className={`card-enter bg-white rounded-2xl overflow-hidden border card-shadow flex flex-col transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-xl ${ad.boosted ? "border-findo-red ring-2 ring-findo-red/30" : "border-border"}`} style={{ animationDelay: `${Math.min(index, 11) * 55}ms` }} data-testid="ad-card">
      <div className="relative h-36 bg-slate-100">
        {ad.image_base64 ? (
          <img src={ad.image_base64} alt="" className="w-full h-full object-cover" />
        ) : (
          <CategoryAvatar category={ad.category} iconSize={30} />
        )}
        {ad.boosted && (
          <span className="absolute top-3 right-3 inline-flex items-center gap-1 boost-shine rounded-full bg-findo-red text-white px-2.5 py-1 text-xs font-bold shadow" data-testid="ad-boost-badge">
            <Sparkles size={13} /> {t("an.boosted")}
          </span>
        )}
      </div>
      <div className="p-4 flex flex-col flex-1">
        <p className="text-xs text-findo-red font-semibold">{labelFor(ad.category)}</p>
        <Link to={`/annonces/${ad.id}`} className="font-semibold text-slate-800 mt-1 line-clamp-2 hover:text-findo-navy transition-colors text-sm" data-testid="ad-detail-link">{ad.description}</Link>
        <p className="text-xs text-slate-400 mt-2 flex items-center gap-1"><MapPin size={12} /> {isFound ? t("anf.foundAt") : t("an.lostAt")} {ad.lost_place || ad.city}</p>
        {(ad.match_resolved || ad.restitution_in_progress) ? (
          <div className="mt-3 rounded-full font-semibold text-center py-2 bg-slate-100 text-slate-500 text-sm" data-testid="ad-resolved-badge">
            {ad.restitution_in_progress ? t("an.restitutionInProgress") : t("an.resolved")}
          </div>
        ) : (
          <Button onClick={onContact} className={`mt-3 rounded-full font-semibold ${isFound ? "bg-teal-600 hover:bg-teal-600/90" : "bg-findo-navy hover:bg-findo-navy/90"}`} data-testid="ad-contact-btn">
            {isFound ? t("anf.claim") : t("an.contact")}
          </Button>
        )}
        <ShareButtons ad={ad} t={t} />
        <div className="mt-2 pt-2 border-t border-border/60 flex justify-end">
          <ReportAdButton adId={ad.id} />
        </div>
      </div>
    </div>
  );
}

function ContactDialog({ ad, isFound = false, onClose, t, user }) {
  const [form, setForm] = useState({ finder_first_name: "", finder_last_name: "", finder_email: "", finder_phone: "", message: "", share_consent: false, photo: "" });
  const needsPhoto = !isFound;
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (ad && user) {
      setForm((f) => ({ ...f, finder_first_name: user.first_name || f.finder_first_name, finder_last_name: user.last_name || f.finder_last_name, finder_email: user.email || f.finder_email, finder_phone: user.phone || f.finder_phone }));
    }
  }, [ad, user]);

  async function submit(e) {
    e.preventDefault();
    if (!form.share_consent) { toast.error(t("an.shareConsentError")); return; }
    if (needsPhoto && !form.photo) { toast.error(t("fr.photoError")); return; }
    setBusy(true);
    try {
      await api(`/public-ads/${ad.id}/contact`, { method: "POST", auth: false, body: { ...form, finder_first_name: form.finder_first_name.trim(), finder_last_name: form.finder_last_name.trim(), finder_name: `${form.finder_first_name.trim()} ${form.finder_last_name.trim()}`, finder_email: form.finder_email.trim().toLowerCase() } });
      toast.success(isFound ? t("anf.claimSent") : t("an.sent"));
      onClose();
      setForm({ finder_first_name: "", finder_last_name: "", finder_email: "", finder_phone: "", message: "", share_consent: false, photo: "" });
    } catch (err) { toast.error(err.message); } finally { setBusy(false); }
  }

  return (
    <Dialog open={!!ad} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto" aria-describedby={undefined} data-testid="ad-contact-dialog">
        <DialogHeader><DialogTitle icon={isFound ? HandHeart : PackageCheck} className="font-display">{isFound ? t("anf.claimTitle") : t("an.contactTitle")}</DialogTitle></DialogHeader>
        {ad && (
          <form onSubmit={submit} className="findo-numbered space-y-5">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>{t("hc.firstName")}</Label><Input required autoComplete="given-name" value={form.finder_first_name} onChange={(e) => setForm({ ...form, finder_first_name: e.target.value })} className="mt-1.5 rounded-xl" data-testid="finder-first-name" /></div>
              <div><Label>{t("hc.lastName")}</Label><Input required autoComplete="family-name" value={form.finder_last_name} onChange={(e) => setForm({ ...form, finder_last_name: e.target.value })} className="mt-1.5 rounded-xl" data-testid="finder-last-name" /></div>
            </div>
            <div><Label>{t("an.finderEmail")}</Label><Input type="email" required value={form.finder_email} onChange={(e) => setForm({ ...form, finder_email: e.target.value })} className="mt-1.5 rounded-xl" data-testid="finder-email" /></div>
            <div><Label>{t("an.finderPhone")}</Label><Input required value={form.finder_phone} onChange={(e) => setForm({ ...form, finder_phone: e.target.value })} className="mt-1.5 rounded-xl" data-testid="finder-phone" /></div>
            {needsPhoto && (
              <div data-testid="finder-photo-field">
                <Label>{t("fr.photoLabel")} <span className="text-findo-red">*</span></Label>
                <p className="text-xs text-slate-500 mt-0.5 mb-1.5">{t("fr.photoHint")}</p>
                <ImageUpload secure="finder" value={form.photo} onChange={(v) => setForm((f) => ({ ...f, photo: v }))} testid="finder-photo" />
              </div>
            )}
            <div><Label>{t("an.message")}</Label><Textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="mt-1.5 rounded-xl" data-testid="finder-message" /></div>
            <label className="flex items-start gap-2.5 rounded-xl bg-slate-50 border border-border p-3 cursor-pointer" data-testid="finder-consent-label">
              <input type="checkbox" checked={form.share_consent} onChange={(e) => setForm({ ...form, share_consent: e.target.checked })} className="mt-0.5 h-4 w-4 accent-findo-navy" data-testid="finder-consent" />
              <span className="text-xs text-slate-600 leading-relaxed">{isFound ? t("anf.shareConsent") : t("an.shareConsent")}</span>
            </label>
            <Button type="submit" disabled={busy || !form.share_consent || (needsPhoto && !form.photo)} className="w-full rounded-xl font-semibold bg-findo-navy hover:bg-findo-navy/90" data-testid="finder-submit">
              {busy ? <FindoSpinner size={16} /> : t("an.send")}
            </Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
